import { useMemo, useState } from "react";
import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { Input, InputGroup } from "../Catalyst/input";
import { AccessibleOrganization } from "../../store/slices/OrganizationsV3Slice";
import { getOrganizationName } from "../pages/Organizations/utils";

// Keeps the orgs whose name matches `term` plus all of their ancestors, so a
// match still renders under its parents. Order (depth-first) is preserved.
// Ancestors come from the list order and `depth`, not `parentOrgId`, which can
// point at a deactivated org that is not in the list.
export const filterOrganizationOptions = (
  options: AccessibleOrganization[],
  term: string
): AccessibleOrganization[] => {
  const query = term.trim().toLowerCase();
  if (!query) return options;

  const keep = new Set<number>();
  // path[d] = the most recent org seen at depth d, i.e. the current ancestry.
  const path: AccessibleOrganization[] = [];
  options.forEach((org) => {
    path.length = Math.min(path.length, org.depth);
    path.push(org);
    if (getOrganizationName(org).toLowerCase().includes(query)) {
      path.forEach((ancestor) => keep.add(ancestor.id));
    }
  });
  return options.filter((org) => keep.has(org.id));
};

interface OrgSelectProps {
  options: AccessibleOrganization[];
  value: number | null;
  onChange: (orgId: number | null) => void;
  // "menu": the filter dropdown (check mark, optional "All" row).
  // "list": the move modal (org icon + checkbox per row, per the Figma).
  variant?: "menu" | "list";
  // Label of the null option; omitted = no null option (an org must be picked).
  allLabel?: string;
  isLoading?: boolean;
  // The org list request failed; shown instead of a misleading "no results".
  isError?: boolean;
  searchPlaceholder?: string;
  className?: string;
}

// Searchable single-select over the org hierarchy, indented by depth. Shared by
// the global user list's Organization filter and Move dialog (ADM-89) and meant
// for the invite modal's Organization field (ADM-92).
const OrgSelect = ({
  options,
  value,
  onChange,
  variant = "menu",
  allLabel,
  isLoading = false,
  isError = false,
  searchPlaceholder = "Search",
  className,
}: OrgSelectProps) => {
  const [term, setTerm] = useState("");
  const visible = useMemo(() => filterOrganizationOptions(options, term), [options, term]);

  const renderRow = (id: number | null, label: string, depth: number) => {
    const selected = value === id;
    return (
      <button
        key={id ?? "all"}
        type="button"
        role="option"
        aria-selected={selected}
        onClick={() => onChange(id)}
        style={{ paddingLeft: `${12 + depth * 24}px` }}
        className={`flex w-full items-center gap-3 rounded py-3 pr-3 text-left text-base leading-6 transition-colors ${
          selected && variant === "menu" ? "bg-[#fafafa] text-[#101828]" : "text-[#475467] hover:bg-gray-50"
        }`}
      >
        {variant === "list" && (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-shadowborder bg-white shadow-light-sm">
            <BuildingOffice2Icon className="h-5 w-5 text-texthead" aria-hidden="true" />
          </span>
        )}
        <span className={`flex-1 truncate ${variant === "list" ? "text-sm font-medium text-texthead" : ""}`}>
          {label}
        </span>
        {variant === "menu" && selected && <CheckIcon className="h-5 w-5 shrink-0 text-[#101828]" />}
        {variant === "list" && (
          <span
            aria-hidden="true"
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
              selected ? "border-green-600 bg-green-500 text-white" : "border-zinc-300 bg-white"
            }`}
          >
            {selected && <CheckIcon className="h-4 w-4" />}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={className}>
      <InputGroup>
        <MagnifyingGlassIcon />
        <Input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          autoFocus
        />
      </InputGroup>
      <div role="listbox" className="mt-2 flex max-h-72 flex-col gap-1 overflow-y-auto">
        {allLabel && !term.trim() && renderRow(null, allLabel, 0)}
        {isLoading ? (
          <p className="px-3 py-3 text-sm text-textColor">Loading organizations...</p>
        ) : isError ? (
          <p className="px-3 py-3 text-sm font-medium text-red-500">
            Could not load organizations. Please refresh the page and try again.
          </p>
        ) : visible.length === 0 ? (
          <p className="px-3 py-3 text-sm text-textColor">No organizations found</p>
        ) : (
          visible.map((org) => renderRow(org.id, getOrganizationName(org), org.depth))
        )}
      </div>
    </div>
  );
};

export default OrgSelect;
